/*
 * vpsUser.ts: Vue Pinia Store
 *
 * defineStore() accepts two distinct values for its second argument:
 * a Setup function or an Options object.
 *
 * Options Stores
 * Similar to Vue's Options API, we can also pass an Options Object with state,
 * actions, and getters properties.
 *
 * state => data;
 * getters => computed properties;
 * actions => methods;
 *
 * Setup Stores
 * There is also another possible syntax to define stores. Similar to the
 * Vue Composition API's setup function, we can pass in a function that
 * defines reactive properties and methods and returns an object with the
 * properties and methods we want to expose.
 *
 * ref()s => state properties;
 * computed()s => getters;
 * function()s => actions;
 *
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * NOTE you must return all state properties in setup stores for pinia to pick
 * them up as state. In other words, you cannot have private state properties
 * in stores.
 * ###########################################################################
 */
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
// import jwt_decode from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';
import { eLCeSystemAPI } from 'src/boot/axios';
import { eTypeCRUDOps, eUserOpsUpdateFrom } from 'src/config/config.enum.eSystem';
import { msGuestUserPortraitBase64JPG, msPSTraineePortraitBase64JPG } from 'src/config/config.img.base64';
import type { mtAxiosReturn } from 'src/config/config.type.axios';
import { moAxiosReturn } from 'src/config/config.type.axios';
import { vpseSystem } from 'src/stores/vpseSystem';
import { mcUtils } from 'src/utils/utils';
import type { mtJWTPayloadAccess } from 'src/user/dto/user-JWT.type';
import { moJWTPayloadAccessInit } from 'src/user/dto/user-JWT.type';
import type { mtUserDto } from 'src/user/dto/user.dto';
import { moUserInit } from 'src/user/dto/user.dto';
import type { mtUserStaffBUDto } from 'src/user/dto/userStafBU.dto';
import type { mtUserStaffPositionDto } from 'src/user/dto/userStaffPosition.dto';
import type { mtUserOpsStaffVOAODto, mtUserOpsPortraitDto } from 'src/user/dto/user-ops.dto';
import { moUserOpsPortraitInit } from 'src/user/dto/user-ops.dto';
import type { mtUserQualificationLUCategoryDto } from 'src/user/dto/userQualificationLUCategory.dto';
import { moUserQualificationLUCategoryInit } from 'src/user/dto/userQualificationLUCategory.dto';
import type { mtUserQualificationLUClassDto } from 'src/user/dto/userQualificationLUClass.dto';
import { moUserQualificationLUClassInit } from 'src/user/dto/userQualificationLUClass.dto';
//
export const vpsUser = defineStore('vpsUser', () => {
  // Store eSystem
  const mpseSystem = vpseSystem();
  // PS Trainee NameLogin Regular Expression: DT?#####
  const mxPSTraineeNameLoginRegEx = /^DT[A-Z]\d\d\d\d\d$/;
  // State variables
  const vrsJWTAccess = ref('');
  const vrsJWTRefresh = ref('');
  const vroJWTPayloadAccess = ref<mtJWTPayloadAccess>({ ...moJWTPayloadAccessInit });
  const vrbOptionUserEnum = ref(false);
  const vraOptionUserStatus = ref<string[]>([]); // list of User Status
  const vraOptionUserType = ref<string[]>([]); // list of User Type
  const vraOptionUserRole = ref<string[]>([]); // list of User Role
  const vraOptionGender = ref<string[]>([]); // list of Gender
  const vraOptionTypeStaff = ref<string[]>([]); // list of (User) Staff Type
  const vraOptionTypeWorkday = ref<string[]>([]); // list of Work Day Type
  const vrbReferenceUser = ref(false);
  const vraReferenceUserStaffBU = ref<mtUserStaffBUDto[]>([]); // list of existing BU
  const vraReferenceUserStaffPosition = ref<mtUserStaffPositionDto[]>([]); // list of existing Staff Position
  const vraReferenceUsereServicesVO = ref<mtUserOpsStaffVOAODto[]>([]); // list of existing eServices VO
  const vraReferenceUsereServicesAO = ref<mtUserOpsStaffVOAODto[]>([]); // list of existing eServices AO
  const vraReferenceUsereOTClaimVO = ref<mtUserOpsStaffVOAODto[]>([]); // list of existing OT Claim VO
  const vraReferenceUsereOTClaimAO = ref<mtUserOpsStaffVOAODto[]>([]); // list of existing OT Claim AO
  const vraReferenceUsereQAFVO = ref<mtUserOpsStaffVOAODto[]>([]); // list of existing QAF VO
  const vraReferenceUsereQAFAO = ref<mtUserOpsStaffVOAODto[]>([]); // list of existing QAF AO
  const vraReferenceUsereLCVentoryVO = ref<mtUserOpsStaffVOAODto[]>([]); // list of existing eLC Inventory VO
  const vraReferenceUsereLCVentoryAO = ref<mtUserOpsStaffVOAODto[]>([]); // list of existing eLC Inventory AO
  const vraReferenceUserQualificationLUCategory = ref<mtUserQualificationLUCategoryDto[]>([]);
  const vraReferenceUserQualificationLUClass = ref<mtUserQualificationLUClassDto[]>([]);
  //
  const vrbUserMyProfileOK = ref(false);
  const vroUserMyProfile = ref<mtUserDto>({ ...moUserInit });
  const vrsUserMyPortraitBase64 = ref(msGuestUserPortraitBase64JPG);
  //
  const vroAdminUserOpsPortrait = ref<mtUserOpsPortraitDto>({ ...moUserOpsPortraitInit });
  //
  const vreAdminUserOps = ref<eTypeCRUDOps>(eTypeCRUDOps.eNOOPS);
  const vreAdminUserOpsUpdateFrom = ref<eUserOpsUpdateFrom>(eUserOpsUpdateFrom.eUndefined);
  const vrnAdminUserOpsID = ref(0);
  //
  const vreAdminUserQualificationLUCategoryOps = ref<eTypeCRUDOps>(eTypeCRUDOps.eNOOPS);
  const vrnAdminUserQualificationLUCategoryID = ref(0);
  //
  const vreAdminUserQualificationLUClassOps = ref<eTypeCRUDOps>(eTypeCRUDOps.eNOOPS);
  const vrnAdminUserQualificationLUClassID = ref(0); //
  //
  const vreAdminUserLicenseClassOps = ref<eTypeCRUDOps>(eTypeCRUDOps.eNOOPS);
  const vrnAdminUserLicenseClassID = ref(0); // when Ops === eUPDATE, tUserLicenseClass.cID
  //
  const vreAdminUserStaffBUOps = ref<eTypeCRUDOps>(eTypeCRUDOps.eNOOPS);
  const vrnAdminUserStaffBUID = ref(0); // when Ops === eUPDATE, tUserStaffBU.cID
  //
  const vreAdminUserStaffPositionOps = ref<eTypeCRUDOps>(eTypeCRUDOps.eNOOPS);
  const vrnAdminUserStaffPositionID = ref(0); // when Ops === eUPDATE, tUserStaffPosition.cID
  //
  const vreAdminUserLUUserRoleOps = ref<eTypeCRUDOps>(eTypeCRUDOps.eNOOPS);
  const vrnAdminUserLUUserRoleID = ref(0);
  // Table Search Filter
  const vrsAdminUserListTableSearch = ref('');
  const vrsAdminHRUserListTableSearch = ref('');
  const vrsAdminUserStaffBUListTableSearch = ref('');
  const vrsAdminUserStaffPositionListTableSearch = ref('');
  const vrsAdminUserLUUserRoleListTableSearch = ref('');
  const vrsUserQualificationLUClassListTableSearch = ref('');
  const vrsUserQualificationLUCategoryListTableSearch = ref('');
  //
  // Local function
  //
  function lfUserReset(): void {
    //
    vrsJWTAccess.value = '';
    vrsJWTRefresh.value = '';
    vroJWTPayloadAccess.value = { ...moJWTPayloadAccessInit };
    //
    vrbOptionUserEnum.value = false;
    vrbReferenceUser.value = false;
    vroUserMyProfile.value = { ...moUserInit };
    vrbUserMyProfileOK.value = false;
  }
  //
  function lfUserFindNameFull(pnIDUser: number, paReferenceUserStaffVOAO: mtUserOpsStaffVOAODto[]): string {
    let lsReturn = pnIDUser.toString() + ' - eUNDEFINED';
    //
    const loUserStaffVOAO = paReferenceUserStaffVOAO.find((poElement) => {
      return poElement.cpinID === pnIDUser;
    });
    if (undefined !== loUserStaffVOAO) {
      lsReturn = pnIDUser.toString() + ' - ' + loUserStaffVOAO.cpisNameFull;
    }
    //
    return lsReturn;
  }
  //
  function mfResetUserEnumOption(): void {
    vrbOptionUserEnum.value = false;
    return;
  }
  //
  async function mfInitUserEnumOption(): Promise<mtAxiosReturn> {
    let loAxiosReturn: mtAxiosReturn;
    if (true === vrbOptionUserEnum.value) {
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetAlreadyInit();
      return loAxiosReturn;
    }
    //
    const loAxiosConfig: AxiosRequestConfig = {
      url: '/user/enumoption',
      method: 'get',
      data: {},
    };
    try {
      const loAxiosResponse: AxiosResponse = await eLCeSystemAPI.request(loAxiosConfig);
      // Get OK: arrays of enumerators
      vrbOptionUserEnum.value = true;
      vraOptionUserStatus.value = loAxiosResponse.data.cpiaOptionUserStatus;
      vraOptionUserType.value = loAxiosResponse.data.cpiaOptionUserType;
      vraOptionUserRole.value = loAxiosResponse.data.cpiaOptionUserRole;
      vraOptionGender.value = loAxiosResponse.data.cpiaOptionGender;
      vraOptionTypeStaff.value = loAxiosResponse.data.cpiaOptionTypeStaff;
      vraOptionTypeWorkday.value = loAxiosResponse.data.cpiaOptionTypeWorkday;
      //
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnOK(loAxiosResponse);
      //
      // console.log('vpsUser.ts-mfInitUserEnumOption-vraOptionsUserRole: ', vraOptionUserRole.value);
      //
    } catch (poError: unknown) {
      vrbOptionUserEnum.value = false;
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnError(poError);
    }
    //
    return loAxiosReturn;
  }
  //
  async function mfInitUserReference(): Promise<mtAxiosReturn> {
    let loAxiosReturn: mtAxiosReturn;
    if (true === vrbReferenceUser.value) {
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetAlreadyInit();
      return loAxiosReturn;
    }
    const loAxiosConfig = {
      url: '/user/reference',
      method: 'get',
      data: {},
    };
    try {
      const loAxiosResponse: AxiosResponse = await eLCeSystemAPI.request(loAxiosConfig);
      // Get OK
      vrbReferenceUser.value = true;
      vraReferenceUserStaffPosition.value = loAxiosResponse.data.cpiaUserStaffPosition;
      vraReferenceUserStaffBU.value = loAxiosResponse.data.cpiaUserStaffBU;
      vraReferenceUsereServicesVO.value = [...loAxiosResponse.data.cpiaUsereServicesVO];
      vraReferenceUsereServicesAO.value = [...loAxiosResponse.data.cpiaUsereServicesAO];
      vraReferenceUsereOTClaimVO.value = [...loAxiosResponse.data.cpiaUsereOTClaimVO];
      vraReferenceUsereOTClaimAO.value = [...loAxiosResponse.data.cpiaUsereOTClaimAO];
      vraReferenceUsereQAFVO.value = [...loAxiosResponse.data.cpiaUsereQAFVO];
      vraReferenceUsereQAFAO.value = [...loAxiosResponse.data.cpiaUsereQAFAO];
      vraReferenceUsereLCVentoryVO.value = [...loAxiosResponse.data.cpiaUsereLCVentoryVO];
      vraReferenceUsereLCVentoryAO.value = [...loAxiosResponse.data.cpiaUsereLCVentoryAO];
      // QualificationLUCategory
      const loUserQualificationLUCategoryZero = { ...moUserQualificationLUCategoryInit };
      loUserQualificationLUCategoryZero.cpinID = 0;
      vraReferenceUserQualificationLUCategory.value = [
        loUserQualificationLUCategoryZero,
        ...loAxiosResponse.data.cpiaUserQualificationLUCategory,
      ];
      // QualificationLUClass
      const loUserQualificationLUClassZero = { ...moUserQualificationLUClassInit };
      loUserQualificationLUClassZero.cpinID = 0;
      vraReferenceUserQualificationLUClass.value = [
        loUserQualificationLUClassZero,
        ...loAxiosResponse.data.cpiaUserQualificationLUClass,
      ];
      // QualificationLUClass - prepend QualifcationLUCategory's UX to QualificationLUClass's UX
      vraReferenceUserQualificationLUClass.value.forEach((poQualificationLUClass) => {
        const loQualificationLUCategory = vraReferenceUserQualificationLUCategory.value.find(
          (poQualificationLUCategory) => {
            if (poQualificationLUCategory.cpinID === poQualificationLUClass.cpinIDUserQualificationLUCategory) {
              return true;
            }
            return false;
          },
        );
        if (undefined !== loQualificationLUCategory) {
          poQualificationLUClass.cpisUX = loQualificationLUCategory.cpisUX + ' - ' + poQualificationLUClass.cpisUX;
        } else {
          poQualificationLUClass.cpisUX = 'UNDEFINED! - ' + poQualificationLUClass.cpisUX;
        }
      });
      //
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnOK(loAxiosResponse);
      //
    } catch (poError: unknown) {
      vrbReferenceUser.value = false;
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnError(poError);
    }
    //
    return loAxiosReturn;
  }
  //
  async function mfHandleAuthLocalLogin(psUsername: string, psPassword: string): Promise<mtAxiosReturn> {
    let loAxiosReturn: mtAxiosReturn;
    const loAxiosConfig: AxiosRequestConfig = {
      url: '/auth/local/login',
      method: 'post',
      data: {
        username: psUsername,
        password: psPassword,
      },
    };
    try {
      const loAxiosResponse: AxiosResponse = await eLCeSystemAPI.request(loAxiosConfig);
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnOK(loAxiosResponse, false);
      // Login OK
      vrsJWTAccess.value = loAxiosResponse.data.cpisJWTAccess;
      vrsJWTRefresh.value = loAxiosResponse.data.cpisJWTRefresh;
      // vroJWTPayloadAccess.value = jwt_decode(loAxiosResponse.data.cpisJWTAccess);
      vroJWTPayloadAccess.value = jwtDecode(loAxiosResponse.data.cpisJWTAccess);
      // Mark Login flag and setup eSystemCatModOps;
      // NOTE: This step need to be done here as subsequent API call will check
      // this flag whether to get ACCESS JWT!!!
      mpseSystem.mfSetLogin(true);
      // Init User Profile, Portrait
      vrbOptionUserEnum.value = false;
      vrbReferenceUser.value = false;
      vrbUserMyProfileOK.value = false;
      await mfHandleUserMyProfile();
      await mfHandleUserMyPortrait();
      // Goto appropriate landing page
      if (
        // - eLC eServices: eServicesUser
        undefined !==
        vroUserMyProfile.value.cpiaUserRole.find((lsUserRole) => {
          if ('eServicesUser'.toLowerCase() === lsUserRole.toLowerCase()) {
            return true;
          }
          return false;
        })
      ) {
        mpseSystem.mfSetCategorySelected('eSERVICES');
      } else if (
        // - POWERsteer Trainee: ePOWERsteerTrainee
        undefined !==
        vroUserMyProfile.value.cpiaUserRole.find((lsUserRole) => {
          if ('ePOWERsteerTrainee'.toLowerCase() === lsUserRole.toLowerCase()) {
            return true;
          }
          return false;
        })
      ) {
        mpseSystem.mfSetCategorySelected('ePOWERsteerTrainee');
      } else {
        // - eLC Pte Ltd: default
        mpseSystem.mfSetCategorySelected('eLCPteLtd');
      }
    } catch (poError: unknown) {
      lfUserReset();
      mpseSystem.mfSetLogin(false);
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnError(poError);
    }
    //
    return loAxiosReturn;
  }
  //
  async function mfHandleAuthRefresh(): Promise<mtAxiosReturn> {
    //
    // Call by axios.ts-eLCeSystemAPI.interceptors.response.use();
    //
    let loAxiosReturn: mtAxiosReturn;
    const loAxiosConfig = {
      url: '/auth/refresh',
      method: 'put',
      data: {
        cpisNameLogin: vroJWTPayloadAccess.value.cpisNameLogin,
      },
    };
    //
    try {
      const loAxiosResponse: AxiosResponse = await eLCeSystemAPI.request(loAxiosConfig);
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnOK(loAxiosResponse);
      // Refresh OK
      vrsJWTAccess.value = loAxiosResponse.data.cpisJWTAccess;
      vrsJWTRefresh.value = loAxiosResponse.data.cpisJWTRefresh;
      // vroJWTPayloadAccess.value = jwt_decode(loAxiosResponse.data.cpisJWTAccess);
      vroJWTPayloadAccess.value = jwtDecode(loAxiosResponse.data.cpisJWTAccess);
    } catch (poError: unknown) {
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnError(poError);
    }
    //
    return loAxiosReturn;
  }
  //
  async function mfHandleAuthLogout(): Promise<mtAxiosReturn> {
    let loAxiosReturn: mtAxiosReturn;
    const loAxiosConfig = {
      url: '/auth/logout',
      method: 'get',
      data: {},
    };
    try {
      const loAxiosResponse = await eLCeSystemAPI.request(loAxiosConfig);
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnOK(loAxiosResponse);
      // Logout OK
      loAxiosReturn.cpisMessage = loAxiosResponse.data.cpisMessage + ' : ' + loAxiosReturn.cpisMessage;
    } catch (poError: unknown) {
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnError(poError);
    }
    // Perform logout process...
    lfUserReset();
    mpseSystem.mfSetLogin(false);
    mpseSystem.mfSetCategorySelected('eLCPteLtd');
    vrsUserMyPortraitBase64.value = msGuestUserPortraitBase64JPG;
    //
    return loAxiosReturn;
  }
  //
  async function mfHandleUserMyProfile(): Promise<mtAxiosReturn> {
    let loAxiosReturn: mtAxiosReturn;
    if (true === vrbUserMyProfileOK.value) {
      // Get once upon each login
      loAxiosReturn = mcUtils.cpsoAxios.cpifGetAlreadyInit();
      return loAxiosReturn;
    }
    //
    if (false === mfIsPSTrainee()) {
      // Normal User
      const loAxiosConfig = {
        url: `/user/${vroJWTPayloadAccess.value?.sub}`,
        method: 'get',
        data: {},
      };
      try {
        const loAxiosResponse: AxiosResponse = await eLCeSystemAPI.request(loAxiosConfig);
        loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnOK(loAxiosResponse);
        // Get OK
        vrbUserMyProfileOK.value = true;
        vroUserMyProfile.value = { ...loAxiosResponse.data.cpioUser };
        await mfInitUserEnumOption();
        await mfInitUserReference();
        if (vroUserMyProfile.value) {
          // convert date and array to string for q-input
          vroUserMyProfile.value.cpisDateStart = mcUtils.cpsoQuasarDate.cpif2YYYYMMMDD(
            vroUserMyProfile.value?.cpidDateStart.toString(),
          );
          vroUserMyProfile.value.cpisDateEnd = mcUtils.cpsoQuasarDate.cpif2YYYYMMMDD(
            vroUserMyProfile.value?.cpidDateEnd?.toString(),
          );
          vroUserMyProfile.value.cpisDOB = mcUtils.cpsoQuasarDate.cpif2YYYYMMMDD(
            vroUserMyProfile.value?.cpidDOB?.toString(),
          );
          //
          vroUserMyProfile.value.cpiaUserStaff.forEach((poUserStaff) => {
            poUserStaff.cpisDateStart = mcUtils.cpsoQuasarDate.cpif2YYYYMMMDD(poUserStaff.cpidDateStart?.toString());
            poUserStaff.cpisDateEnd = mcUtils.cpsoQuasarDate.cpif2YYYYMMMDD(poUserStaff.cpidDateEnd?.toString());
          });
          //
          vroUserMyProfile.value.cpiaUserQualification.forEach((poUserQualification) => {
            poUserQualification.cpisDateStart = mcUtils.cpsoQuasarDate.cpif2YYYYMMMDD(
              poUserQualification.cpidDateStart?.toString(),
            );
            poUserQualification.cpisDateEnd = mcUtils.cpsoQuasarDate.cpif2YYYYMMMDD(
              poUserQualification.cpidDateEnd?.toString(),
            );
          });
        }
      } catch (poError: unknown) {
        vrbUserMyProfileOK.value = false;
        loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnError(poError);
      }
    } else {
      // PS Trainee
      const loAxiosConfig = {
        url: '/powersteer/trainee/operate',
        method: 'put',
        data: {
          cpinID: vroJWTPayloadAccess.value.sub,
        },
      };
      try {
        const loAxiosResponse: AxiosResponse = await eLCeSystemAPI.request(loAxiosConfig);
        loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnOK(loAxiosResponse);
        // Get OK
        vrbUserMyProfileOK.value = true;
        vroUserMyProfile.value = { ...moUserInit };
        vroUserMyProfile.value = { ...loAxiosResponse.data.cpioPSTraineePut };
      } catch (poError: unknown) {
        vrbUserMyProfileOK.value = false;
        loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnError(poError);
      }
    }
    //
    return loAxiosReturn;
  }
  //
  async function mfHandleUserMyPortrait(): Promise<mtAxiosReturn> {
    let loAxiosReturn: mtAxiosReturn;
    if (false === mfIsPSTrainee()) {
      // Normal User
      const loAxiosConfig = {
        url: '/user/portrait',
        method: 'put', // NOTE: Get with body NOGO! Use Put for Get with body!!!
        data: {
          cpinID: vroJWTPayloadAccess.value?.sub,
        },
      };
      try {
        const loAxiosResponse: AxiosResponse = await eLCeSystemAPI.request(loAxiosConfig);
        loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnOK(loAxiosResponse);
        // Get OK
        vrsUserMyPortraitBase64.value = loAxiosResponse.data.cpisUserPortraitBase64;
        //
      } catch (poError: unknown) {
        loAxiosReturn = mcUtils.cpsoAxios.cpifGetReturnError(poError);
        // Hard code to eLC-GUEST portrait!!!
        vrsUserMyPortraitBase64.value = msGuestUserPortraitBase64JPG;
      }
    } else {
      // PS Trainee
      loAxiosReturn = { ...moAxiosReturn };
      vrsUserMyPortraitBase64.value = msPSTraineePortraitBase64JPG;
    }
    //
    return loAxiosReturn;
  }
  //
  function mfSetAdminUserOpsPortrait(
    pnID: number,
    psNameLogin: string,
    psNameFull: string,
    psNameDisplay: string,
    peUserUpdateFrom: eUserOpsUpdateFrom,
  ): void {
    vroAdminUserOpsPortrait.value = {
      cpinID: pnID,
      cpisNameLogin: psNameLogin,
      cpisNameFull: psNameFull,
      cpisNameDisplay: psNameDisplay,
      cpieUserUpdateFrom: peUserUpdateFrom,
    };
  }
  //
  function mfSetAdminUserOps(
    peAdminUserOps: eTypeCRUDOps,
    peAdminUserOpsUpdateFrom: eUserOpsUpdateFrom,
    pnAdminUserOpsID: number,
  ) {
    vreAdminUserOps.value = peAdminUserOps;
    vreAdminUserOpsUpdateFrom.value = peAdminUserOpsUpdateFrom;
    vrnAdminUserOpsID.value = pnAdminUserOpsID;
  }
  //
  function mfSetAdminUserQualificationLUCategoryOps(
    peAdminUserQualificationLUCategoryOps: eTypeCRUDOps,
    pnAdminUserQualificationLUCategoryID: number,
  ) {
    vreAdminUserQualificationLUCategoryOps.value = peAdminUserQualificationLUCategoryOps;
    vrnAdminUserQualificationLUCategoryID.value = pnAdminUserQualificationLUCategoryID;
  }
  //
  function mfSetAdminUserQualificationLUClassOps(
    peAdminUserQualificationLUClassOps: eTypeCRUDOps,
    pnAdminUserQualificationLUClassID: number,
  ) {
    vreAdminUserQualificationLUClassOps.value = peAdminUserQualificationLUClassOps;
    vrnAdminUserQualificationLUClassID.value = pnAdminUserQualificationLUClassID;
  }
  //
  function mfSetAdminUserLicenseClassOps(peAdminUserLicenseClassOps: eTypeCRUDOps, pnAdminUserLicenseClassID: number) {
    vreAdminUserLicenseClassOps.value = peAdminUserLicenseClassOps;
    vrnAdminUserLicenseClassID.value = pnAdminUserLicenseClassID;
  }
  //
  function mfSetAdminUserStaffBUOps(peAdminUserStaffBUOps: eTypeCRUDOps, pnAdminUserStaffBUID: number) {
    vreAdminUserStaffBUOps.value = peAdminUserStaffBUOps;
    vrnAdminUserStaffBUID.value = pnAdminUserStaffBUID;
  }
  //
  function mfSetAdminUserStaffPositionOps(
    peAdminUserStaffPositionOps: eTypeCRUDOps,
    pnAdminUserStaffPositionID: number,
  ) {
    vreAdminUserStaffPositionOps.value = peAdminUserStaffPositionOps;
    vrnAdminUserStaffPositionID.value = pnAdminUserStaffPositionID;
  }
  //
  function mfSetAdminUserLUUserRoleOps(peAdminUserLUUserRoleOps: eTypeCRUDOps, pnAdminUserLUUserRoleID: number) {
    vreAdminUserLUUserRoleOps.value = peAdminUserLUUserRoleOps;
    vrnAdminUserLUUserRoleID.value = pnAdminUserLUUserRoleID;
  }
  // Check user for specific user role
  function mfIseLCAdmin() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eLCAdmin');
  }
  function mfIseLCAdminOps() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eLCAdminOps');
  }
  function mfIseLCHRAdmin() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eLCHRAdmin');
  }
  function mfIseLCFinAdmin() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eLCFinAdmin');
  }
  //
  function mfIseLCTechSupport() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eLCTechSupport');
  }
  //
  function mfIseServicesAdmin() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eServicesAdmin');
  }
  function mfIseServicesReport() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eServicesReport');
  }
  function mfIseServicesUser() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eServicesUser');
  }
  //
  function mfIseServicesVO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eServicesVO');
  }
  function mfIseServicesAO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eServicesAO');
  }
  //
  function mfIseOTClaimVO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eOTClaimVO');
  }
  function mfIseOTClaimAO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eOTClaimAO');
  }
  function mfIseOTClaimUser() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eOTClaimUser');
  }
  //
  function mfIseQAFVO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eQAFVO');
  }
  function mfIseQAFAO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eQAFAO');
  }
  //
  function mfIseLCVentoryVO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eLCVentoryVO');
  }
  function mfIseLCVentoryAO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('eLCVentoryAO');
  }
  //
  function mfIsePOWERsupAdmin() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsupAdmin');
  }
  //
  function mfIsePOWERsteerAdminSys() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerAdminSys');
  }
  function mfIsePOWERsteerAdminOps() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerAdminOps');
  }
  function mfIsePOWERsteerReport() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerReport');
  }
  function mfIsePOWERsteerUser() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerUser');
  }
  function mfIsePOWERsteerOwner() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerOwner');
  }
  function mfIsePOWERsteerAuthority() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerAuthority');
  }
  function mfIsePOWERsteerTLS() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerTLS');
  }
  //
  function mfIsePOWERsteerHTF() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerHTF');
  }
  function mfIsePOWERsteerHCT() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerHCT');
  }
  function mfIsePOWERsteerCTO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerCTO');
  }
  function mfIsePOWERsteerTDO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerTDO');
  }
  function mfIsePOWERsteerTMO() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerTMO');
  }
  function mfIsePOWERsteerDTSS() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerDTSS');
  }
  function mfIsePOWERsteerOIL() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerOIL');
  }
  function mfIsePOWERsteerASG() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerASG');
  }
  function mfIsePOWERsteerTrainer() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerTrainer');
  }
  function mfIsePOWERsteerWorkshop() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerWorkshop');
  }
  function mfIsePOWERsteerTrainee() {
    return vroUserMyProfile.value.cpiaUserRole.includes('ePOWERsteerTrainee');
  }
  //
  function mfIsPSTrainee() {
    if (true === mxPSTraineeNameLoginRegEx.test(vroJWTPayloadAccess.value.cpisNameLogin)) {
      return true;
    }
    return false;
  }
  function mfIsPSTraineeLogin(psUserName: string) {
    if (true === mxPSTraineeNameLoginRegEx.test(psUserName)) {
      return true;
    }
    return false;
  }
  // vcr: {v}ue {c}omputed {r}ef
  const vcrGetJWTAccess = computed(() => {
    return vrsJWTAccess.value;
  });
  const vcrGetJWTRefresh = computed(() => {
    return vrsJWTRefresh.value;
  });
  const vcrGetMyPortraitBase64 = computed(() => {
    return vrsUserMyPortraitBase64.value;
  });
  const vcrGetMyUserID = computed(() => {
    return vroJWTPayloadAccess.value.sub;
  });
  const vcrGetMyNameLogin = computed(() => {
    return vroJWTPayloadAccess.value?.cpisNameLogin;
  });
  const vcrGetMyDateStart = computed(() => {
    return vroUserMyProfile.value.cpidDateStart;
  });
  const vcrGetMyNameFull = computed(() => {
    return vroUserMyProfile.value.cpisNameFull;
  });
  const vcrGetMyNameDisplay = computed(() => {
    return vroJWTPayloadAccess.value?.cpisNameDisplay;
  });
  const vcrGetMyUserType = computed(() => {
    return vroJWTPayloadAccess.value?.cpieUserType;
  });
  const vcrGetMyUserRole = computed(() => {
    return vroJWTPayloadAccess.value?.cpiaUserRole;
  });
  const vcrGetMyUserIseServicesVO = computed(() => {
    if (vroJWTPayloadAccess.value) {
      return vroJWTPayloadAccess.value.cpiaUserRole.includes('eServicesVO');
    }
    return false;
  });
  const vcrGetMyUserIseServicesAO = computed(() => {
    if (vroJWTPayloadAccess.value) {
      return vroJWTPayloadAccess.value.cpiaUserRole.includes('eServicesAO');
    }
    return false;
  });
  const vcrGetMyUserIsOTClaimVO = computed(() => {
    if (vroJWTPayloadAccess.value) {
      return vroJWTPayloadAccess.value.cpiaUserRole.includes('eOTClaimVO');
    }
    return false;
  });
  const vcrGetMyUserIsOTClaimAO = computed(() => {
    if (vroJWTPayloadAccess.value) {
      return vroJWTPayloadAccess.value.cpiaUserRole.includes('eOTClaimAO');
    }
    return false;
  });
  const vcrGetMyUserIsQAFVO = computed(() => {
    if (vroJWTPayloadAccess.value) {
      return vroJWTPayloadAccess.value.cpiaUserRole.includes('eQAFVO');
    }
    return false;
  });
  const vcrGetMyUserIsQAFAO = computed(() => {
    if (vroJWTPayloadAccess.value) {
      return vroJWTPayloadAccess.value.cpiaUserRole.includes('eQAFAO');
    }
    return false;
  });
  const vcrGetMyUserIseLCVentoryVO = computed(() => {
    if (vroJWTPayloadAccess.value) {
      return vroJWTPayloadAccess.value.cpiaUserRole.includes('eLCVentoryVO');
    }
    return false;
  });
  const vcrGetMyUserIseLCVentoryAO = computed(() => {
    if (vroJWTPayloadAccess.value) {
      return vroJWTPayloadAccess.value.cpiaUserRole.includes('eLCVentoryAO');
    }
    return false;
  });
  const vcrGetOptionUserStatus = computed(() => {
    return vraOptionUserStatus.value;
  });
  const vcrGetOptionUserType = computed(() => {
    return vraOptionUserType.value;
  });
  const vcrGetOptionUserRole = computed(() => {
    return vraOptionUserRole.value;
  });
  const vcrGetOptionGender = computed(() => {
    return vraOptionGender.value;
  });
  const vcrGetOptionTypeStaff = computed(() => {
    return vraOptionTypeStaff.value;
  });
  const vcrGetOptionTypeWorkday = computed(() => {
    return vraOptionTypeWorkday.value;
  });
  const vcrGetReferenceUserStaffPosition = computed(() => {
    return vraReferenceUserStaffPosition.value;
  });
  const vcrGetReferenceUserStaffBU = computed(() => {
    return vraReferenceUserStaffBU.value;
  });
  const vcrGetReferenceUsereServicesVO = computed(() => {
    return vraReferenceUsereServicesVO.value;
  });
  const vcrGetReferenceUsereServicesAO = computed(() => {
    return vraReferenceUsereServicesAO.value;
  });
  const vcrGetReferenceUsereOTClaimVO = computed(() => {
    return vraReferenceUsereOTClaimVO.value;
  });
  const vcrGetReferenceUsereOTClaimAO = computed(() => {
    return vraReferenceUsereOTClaimAO.value;
  });
  const vcrGetReferenceUsereQAFVO = computed(() => {
    return vraReferenceUsereQAFVO.value;
  });
  const vcrGetReferenceUsereQAFAO = computed(() => {
    return vraReferenceUsereQAFAO.value;
  });
  const vcrGetReferenceUsereLCVentoryVO = computed(() => {
    return vraReferenceUsereLCVentoryVO.value;
  });
  const vcrGetReferenceUsereLCVentoryAO = computed(() => {
    return vraReferenceUsereLCVentoryAO.value;
  });
  const vcrGetReferenceUserQualificationLUCategory = computed(() => {
    return vraReferenceUserQualificationLUCategory.value;
  });
  const vcrGetReferenceUserQualificationLUClass = computed(() => {
    return vraReferenceUserQualificationLUClass.value;
  });
  //
  const vcrGetAdminUserOpsPortrait = computed(() => {
    return vroAdminUserOpsPortrait.value;
  });
  const vcrGetAdminUserOps = computed(() => {
    return {
      cpieAdminUserOps: vreAdminUserOps.value,
      cpieAdminUserOpsUpdateFrom: vreAdminUserOpsUpdateFrom.value,
      cpinAdminUserID: vrnAdminUserOpsID.value,
    };
  });
  const vcrGetAdminUserQualificationLUCategoryOps = computed(() => {
    return {
      cpieAdminUserQualificationLUCategoryOps: vreAdminUserQualificationLUCategoryOps.value,
      cpinAdminUserQualificationLUCategoryID: vrnAdminUserQualificationLUCategoryID.value,
    };
  });
  const vcrGetAdminUserQualificationLUClassOps = computed(() => {
    return {
      cpieAdminUserQualificationLUClassOps: vreAdminUserQualificationLUClassOps.value,
      cpinAdminUserQualificationLUClassID: vrnAdminUserQualificationLUClassID.value,
    };
  });
  const vcrGetAdminUserLicenseClassOps = computed(() => {
    return {
      cpieAdminUserLicenseClassOps: vreAdminUserLicenseClassOps.value,
      cpinAdminUserLicenseClassID: vrnAdminUserLicenseClassID.value,
    };
  });
  const vcrGetAdminUserStaffBUOps = computed(() => {
    return {
      cpieAdminUserStaffBUOps: vreAdminUserStaffBUOps.value,
      cpinAdminUserStaffBUID: vrnAdminUserStaffBUID.value,
    };
  });
  const vcrGetAdminUserStaffPositionOps = computed(() => {
    return {
      cpieAdminUserStaffPositionOps: vreAdminUserStaffPositionOps.value,
      cpinAdminUserStaffPositionID: vrnAdminUserStaffPositionID.value,
    };
  });
  const vcrGetAdminUserLUUserRoleOps = computed(() => {
    return {
      cpieAdminUserLUUserRoleOps: vreAdminUserLUUserRoleOps.value,
      cpinAdminUserLUUserRoleID: vrnAdminUserLUUserRoleID.value,
    };
  });
  // TODO: Need to review the code as VO/AO is change to INTEGER[]!!!
  const vcrGetUserVONameFull = computed(() => {
    return (pnID: number, psModule: string) => {
      switch (psModule) {
        case 'eLEAVE':
        case 'eCLAIM':
          return lfUserFindNameFull(pnID, vraReferenceUsereServicesVO.value);
        // break; // error TS7027: Unreachable code detected.
        case 'eOTCLAIM':
          return lfUserFindNameFull(pnID, vraReferenceUsereOTClaimVO.value);
        // break; // error TS7027: Unreachable code detected.
        case 'eQAF':
          return lfUserFindNameFull(pnID, vraReferenceUsereQAFVO.value);
        // break; // error TS7027: Unreachable code detected.
        case 'eLCVENTORY':
          return lfUserFindNameFull(pnID, vraReferenceUsereLCVentoryVO.value);
        // break; // error TS7027: Unreachable code detected.
        default:
          break;
      }
      return lfUserFindNameFull(pnID, vraReferenceUsereServicesVO.value);
    };
  });
  // TODO: Need to review the code as VO/AO is change to INTEGER[]!!!
  const vcrGetUserAONameFull = computed(() => {
    return (pnID: number, psModule: string) => {
      switch (psModule) {
        case 'eLEAVE':
        case 'eCLAIM':
          return lfUserFindNameFull(pnID, vraReferenceUsereServicesAO.value);
        // break; // error TS7027: Unreachable code detected.
        case 'eOTCLAIM':
          return lfUserFindNameFull(pnID, vraReferenceUsereOTClaimAO.value);
        // break; // error TS7027: Unreachable code detected.
        case 'eQAF':
          return lfUserFindNameFull(pnID, vraReferenceUsereQAFAO.value);
        // break; // error TS7027: Unreachable code detected.
        case 'eLCVENTORY':
          return lfUserFindNameFull(pnID, vraReferenceUsereLCVentoryAO.value);
        // break; // error TS7027: Unreachable code detected.
        default:
          break;
      }
      return lfUserFindNameFull(pnID, vraReferenceUsereServicesAO.value);
    };
  });
  //
  return {
    vrsJWTAccess,
    vrsJWTRefresh,
    vroJWTPayloadAccess,
    vrbOptionUserEnum,
    vraOptionUserStatus,
    vraOptionUserType,
    vraOptionUserRole,
    vraOptionGender,
    vraOptionTypeStaff,
    vraOptionTypeWorkday,
    vrbReferenceUser,
    vraReferenceUserStaffBU,
    vraReferenceUserStaffPosition,
    vraReferenceUsereServicesVO,
    vraReferenceUsereServicesAO,
    vraReferenceUsereOTClaimVO,
    vraReferenceUsereOTClaimAO,
    vraReferenceUsereQAFVO,
    vraReferenceUsereQAFAO,
    vraReferenceUsereLCVentoryVO,
    vraReferenceUsereLCVentoryAO,
    vraReferenceUserQualificationLUCategory,
    vraReferenceUserQualificationLUClass,
    vrbUserMyProfileOK,
    vroUserMyProfile,
    vrsUserMyPortraitBase64,
    vroAdminUserOpsPortrait,
    vreAdminUserOps,
    vreAdminUserOpsUpdateFrom,
    vrnAdminUserOpsID,
    vreAdminUserQualificationLUCategoryOps,
    vrnAdminUserQualificationLUCategoryID,
    vreAdminUserQualificationLUClassOps,
    vrnAdminUserQualificationLUClassID,
    vreAdminUserLicenseClassOps,
    vrnAdminUserLicenseClassID,
    vreAdminUserStaffBUOps,
    vrnAdminUserStaffBUID,
    vreAdminUserStaffPositionOps,
    vrnAdminUserStaffPositionID,
    vreAdminUserLUUserRoleOps,
    vrnAdminUserLUUserRoleID,
    vrsAdminUserListTableSearch,
    vrsAdminHRUserListTableSearch,
    vrsAdminUserStaffBUListTableSearch,
    vrsAdminUserStaffPositionListTableSearch,
    vrsAdminUserLUUserRoleListTableSearch,
    vrsUserQualificationLUClassListTableSearch,
    vrsUserQualificationLUCategoryListTableSearch,
    vcrGetJWTAccess,
    vcrGetJWTRefresh,
    vcrGetMyPortraitBase64,
    vcrGetMyUserID,
    vcrGetMyDateStart,
    vcrGetMyNameLogin,
    vcrGetMyNameFull,
    vcrGetMyNameDisplay,
    vcrGetMyUserType,
    vcrGetMyUserRole,
    vcrGetMyUserIseServicesVO,
    vcrGetMyUserIseServicesAO,
    vcrGetMyUserIsOTClaimVO,
    vcrGetMyUserIsOTClaimAO,
    vcrGetMyUserIsQAFVO,
    vcrGetMyUserIsQAFAO,
    vcrGetMyUserIseLCVentoryVO,
    vcrGetMyUserIseLCVentoryAO,
    vcrGetOptionUserStatus,
    vcrGetOptionUserType,
    vcrGetOptionUserRole,
    vcrGetOptionGender,
    vcrGetOptionTypeStaff,
    vcrGetOptionTypeWorkday,
    vcrGetReferenceUserStaffPosition,
    vcrGetReferenceUserStaffBU,
    vcrGetReferenceUsereServicesVO,
    vcrGetReferenceUsereServicesAO,
    vcrGetReferenceUsereOTClaimVO,
    vcrGetReferenceUsereOTClaimAO,
    vcrGetReferenceUsereQAFVO,
    vcrGetReferenceUsereQAFAO,
    vcrGetReferenceUsereLCVentoryVO,
    vcrGetReferenceUsereLCVentoryAO,
    vcrGetReferenceUserQualificationLUCategory,
    vcrGetReferenceUserQualificationLUClass,
    vcrGetAdminUserOpsPortrait,
    vcrGetAdminUserOps,
    vcrGetAdminUserQualificationLUCategoryOps,
    vcrGetAdminUserQualificationLUClassOps,
    vcrGetAdminUserLicenseClassOps,
    vcrGetAdminUserStaffBUOps,
    vcrGetAdminUserStaffPositionOps,
    vcrGetAdminUserLUUserRoleOps,
    vcrGetUserVONameFull,
    vcrGetUserAONameFull,
    mfResetUserEnumOption,
    mfInitUserEnumOption,
    mfInitUserReference,
    mfHandleAuthLocalLogin,
    mfHandleAuthRefresh,
    mfHandleAuthLogout,
    mfHandleUserMyPortrait,
    mfHandleUserMyProfile,
    mfSetAdminUserOpsPortrait,
    mfSetAdminUserOps,
    mfSetAdminUserQualificationLUCategoryOps,
    mfSetAdminUserQualificationLUClassOps,
    mfSetAdminUserLicenseClassOps,
    mfSetAdminUserStaffBUOps,
    mfSetAdminUserStaffPositionOps,
    mfSetAdminUserLUUserRoleOps,
    mfIseLCAdmin,
    mfIseLCAdminOps,
    mfIseLCHRAdmin,
    mfIseLCFinAdmin,
    mfIseLCTechSupport,
    mfIseServicesAdmin,
    mfIseServicesReport,
    mfIseServicesUser,
    mfIseServicesVO,
    mfIseServicesAO,
    mfIseOTClaimVO,
    mfIseOTClaimAO,
    mfIseOTClaimUser,
    mfIseQAFVO,
    mfIseQAFAO,
    mfIseLCVentoryVO,
    mfIseLCVentoryAO,
    mfIsePOWERsupAdmin,
    mfIsePOWERsteerAdminSys,
    mfIsePOWERsteerAdminOps,
    mfIsePOWERsteerReport,
    mfIsePOWERsteerUser,
    mfIsePOWERsteerOwner,
    mfIsePOWERsteerAuthority,
    mfIsePOWERsteerTLS,
    mfIsePOWERsteerHTF,
    mfIsePOWERsteerHCT,
    mfIsePOWERsteerCTO,
    mfIsePOWERsteerTDO,
    mfIsePOWERsteerTMO,
    mfIsePOWERsteerDTSS,
    mfIsePOWERsteerOIL,
    mfIsePOWERsteerASG,
    mfIsePOWERsteerTrainer,
    mfIsePOWERsteerWorkshop,
    mfIsePOWERsteerTrainee,
    mfIsPSTrainee,
    mfIsPSTraineeLogin,
  };
});
// TODO: Find out how to re-start store with clean state!!!
// NOTE: Try to clear store to remove deleted states, NOT sucessful!!!
// const mpsUser = vpsUser();
// mpsUser.$reset();
